/* assets/bundle_hydrator.js
   Hydrates bundle contents in cart/drawer AND injects a preorder note for parent-only bundles.
   This version:
     • places the preorder note UNDER the bundle pills (inside the bundle block)
     • uses Storefront API to detect continue-selling-when-OOS via `currentlyNotInStock`
     • if ANY component is currentlyNotInStock, injects preorder note on the bundle
     • removes bold text and colors the note #EA6739
     • keeps existing proportional price allocation + rendering
   Requirements:
     • Expose your Storefront token as window.G_TOK
       e.g. in theme.liquid: <script>window.G_TOK = "xxxx";</script>
*/
(function () {
  // ================= MANUAL SETTINGS =================
  // Ship date text for the preorder note
  /* One source: snippets/preorder-config.liquid publishes the theme setting as
   * window.__preorder. The literal below is only a last resort if that snippet is ever
   * dropped — it is not a second place to edit. This used to be a hard-coded 'July 31' that
   * went on being shown for months after the date had passed. */
  const PREORDER_SHIP_DATE = (window.__preorder && window.__preorder.date) || 'September 30';
  /* A SKU on its own timeline overrides the shared date for the bundle it sits in. */
  const preorderDateForSku = (sku) => {
    const map = (window.__preorder && window.__preorder.bySku) || {};
    return map[String(sku || '').trim().toUpperCase()] || PREORDER_SHIP_DATE;
  };

  // Note color (no bold)
  const NOTE_COLOR_HEX = '#EA6739';

  // Debug: add ?debugCart=1 (or ?debug=1) once and it will persist while you browse
  // ===================================================

  // ---------- DEBUG GATE (case-insensitive + persistent) ----------
  const dbgParam = new URLSearchParams(location.search).toString().toLowerCase();
  const dbgNow = dbgParam.includes('debugcart') || dbgParam.includes('debug=1');
  if (dbgNow) { try { localStorage.setItem('bh_debug', '1'); } catch {} }
  const shouldDebug = () => {
    try { return localStorage.getItem('bh_debug') === '1'; } catch { return dbgNow; }
  };
  const log = (...args) => { if (shouldDebug()) console.log('[BundleHydrator]', ...args); };

  // ---------- UTILS ----------
  const qa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const asNumber = (v, d = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : d;
  };

  // Money helpers
  const ROUND_TO_CENTS = 100;          // 100 = $1 increments (change to 1 for cents)
  const ROUND_CHEAPEST_FIRST = true;   // fix rounding drift touching cheapest first
  const roundCents = (n) => Math.round(n / ROUND_TO_CENTS) * ROUND_TO_CENTS;
  const moneyNoCents = (cents) => `$${Math.round((Number(cents) || 0) / 100)}`; // "$12"
  /* Unit prices are not always whole dollars — an allocated charm can land on $2.50 —
   * and rounding one to "$3 ea × 4  $10" reads like an arithmetic error. Show cents only
   * when there are cents, so the multiplication printed on the row always checks out. */
  const moneyAuto = (cents) => {
    const n = Number(cents) || 0;
    return n % 100 === 0 ? `$${n / 100}` : `$${(n / 100).toFixed(2)}`;
  };

  // ---------- DETAILS PARSER ----------
  // Tries JSON first (e.g., [{"id":"gid://shopify/ProductVariant/123","qty":1}, ...]),
  // then falls back to delimited text accepting GraphQL GIDs or bare numeric IDs.
  const parseDetails = (str) => {
    if (!str) return [];
    // JSON first
    try {
      const json = JSON.parse(str);
      const arr = Array.isArray(json) ? json : (json.items || []);
      if (Array.isArray(arr) && arr.length) {
        const out = arr.map(x => {
          const raw = String(x.id || x.variant_id || '').trim();
          const gid = raw.match(/ProductVariant\/(\d{6,})/i);
          const id  = gid ? gid[1] : (raw.match(/\d{6,}/) ? raw.match(/\d{6,}/)[0] : null);
          const qty = Number(x.qty || x.quantity || 1) || 1;
          return id ? { id, qty: Math.max(1, qty) } : null;
        }).filter(Boolean);
        if (out.length) return out;
      }
    } catch { /* not JSON; continue */ }

    // Delimited fallback
    const cleaned = String(str).replace(/&amp;/g, '&').trim();
    const chunks = cleaned.split(/[,;|\n]+/);
    const items = [];
    for (const raw of chunks) {
      const chunk = raw.trim();
      if (!chunk) continue;
      const gidMatch = chunk.match(/gid:\/\/shopify\/ProductVariant\/(\d{6,})/i);
      const idMatch  = chunk.match(/(^|\D)(\d{6,})(\D|$)/);
      const id = gidMatch ? gidMatch[1] : (idMatch ? idMatch[2] : null);
      const qtyMatch = chunk.match(/(?:x|×|:|-)\s*(\d+)/i);
      const qty = qtyMatch ? Math.max(1, parseInt(qtyMatch[1], 10)) : 1;
      if (id) items.push({ id, qty });
    }
    return items;
  };

  // ---------- DATA FETCH (variant JSON for image/name/price) ----------
  const fetchVariant = (id) =>
    fetch(`/variants/${id}.js`, { headers: { 'Accept': 'application/json' } })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`Variant ${id} not found`))));

  const imgSrc = (v) => {
    if (v.featured_image) {
      if (typeof v.featured_image === 'string') return v.featured_image;
      if (v.featured_image.src) return v.featured_image.src;
    }
    if (v.image) {
      if (typeof v.image === 'string') return v.image;
      if (v.image.src) return v.image.src;
    }
    return '';
  };

  // ---------- STOREFRONT FLAGS (currentlyNotInStock) ----------
  const STOREFRONT_ENDPOINT = '/api/2025-01/graphql.json';
  const sfFlagsCache = new Map(); // key: numeric variant id -> { availableForSale, currentlyNotInStock }

  const fetchStorefrontFlags = async (variantNumericId) => {
    if (sfFlagsCache.has(variantNumericId)) return sfFlagsCache.get(variantNumericId);

    const token = (window.G_TOK || '').trim();
    if (!token) {
      const fallback = { availableForSale: null, currentlyNotInStock: null, error: 'missing_token' };
      sfFlagsCache.set(variantNumericId, fallback);
      return fallback;
    }

    const gid = `gid://shopify/ProductVariant/${variantNumericId}`;
    const query = `
      query VariantFlags($id: ID!) {
        node(id: $id) {
          ... on ProductVariant {
            id
            availableForSale
            currentlyNotInStock
          }
        }
      }
    `;

    try {
      const res = await fetch(STOREFRONT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token
        },
        body: JSON.stringify({ query, variables: { id: gid } })
      });

      const json = await res.json();
      const node = json?.data?.node || null;

      const out = {
        availableForSale: node ? !!node.availableForSale : null,
        currentlyNotInStock: node ? !!node.currentlyNotInStock : null
      };

      sfFlagsCache.set(variantNumericId, out);
      return out;
    } catch (e) {
      const out = { availableForSale: null, currentlyNotInStock: null, error: String(e && e.message ? e.message : e) };
      sfFlagsCache.set(variantNumericId, out);
      return out;
    }
  };

  // Decides preorder based on Storefront currentlyNotInStock.
  // If Storefront fails/missing token, we fall back to the old heuristic using /variants/{id}.js.
  const isPreorderish = (variantJs, sfFlags) => {
    // A ready-to-ship colour ships now even at zero stock (snippets/preorder-config.liquid).
    if (window.__preorder && window.__preorder.isReadyToShip && window.__preorder.isReadyToShip(variantJs)) return false;
    // Primary: Storefront `currentlyNotInStock`
    if (sfFlags && sfFlags.currentlyNotInStock === true) {
      log('variant preorder (storefront)', { id: variantJs.id, currentlyNotInStock: true });
      return true;
    }

    // If storefront explicitly says false, then it's not preorder by this rule.
    if (sfFlags && sfFlags.currentlyNotInStock === false) {
      log('variant preorder (storefront)', { id: variantJs.id, currentlyNotInStock: false });
      return false;
    }

    // Fallback: old heuristic (useful if token missing or Storefront is blocked)
    const invMgmt   = variantJs.inventory_management || null;                 // "shopify" | null
    const qtyRaw    = (variantJs.inventory_quantity ?? null);                 // may be null/hidden
    const hasQty    = qtyRaw !== null && qtyRaw !== undefined && Number.isFinite(Number(qtyRaw));
    const qty       = hasQty ? Number(qtyRaw) : null;
    const available = !!variantJs.available;

    log('variant check (fallback)', { id: variantJs.id, invMgmt, qty, available, sfFlags });

    if (invMgmt && hasQty) return qty <= 0;
    if (available === false) return true;
    return false;
  };

  // Insert note **inside** the bundle block, *before* the pills list
  const injectPreorderNote = (wrap, shipDate) => {
    if (wrap.dataset.preorderNoteInjected === '1') return;
    wrap.dataset.preorderNoteInjected = '1';

    const list = wrap.querySelector('.bundle-contents__list');

    const note = document.createElement('p');
    note.className = 'cart-item-preorder fs-body-60';
    note.style.marginTop = '0.5rem';
    note.style.marginBottom = '0.5rem';   // adds breathing room before the pills
    note.style.color = NOTE_COLOR_HEX;
    note.style.fontWeight = 'normal';
    note.style.lineHeight = '1.4';
    note.textContent = `Please note: this order contains a preorder item and is estimated to ship ${shipDate || PREORDER_SHIP_DATE}.`;

    // Place ABOVE the pills list
    if (list && list.parentElement) {
      list.parentElement.insertBefore(note, list);
    } else {
      // Fallback: put it at the top of the wrap
      wrap.insertBefore(note, wrap.firstChild);
    }

    log('preorder note injected above pills');
  };

  // Inject note ABOVE the preorder marker for normal items
  const injectPreorderNoteAboveMarker = (markerEl, shipDate) => {
    if (!markerEl || markerEl.dataset.preorderNoteInjected === '1') return;
    markerEl.dataset.preorderNoteInjected = '1';

    const note = document.createElement('p');
    note.className = 'cart-item-preorder fs-body-60';
    note.style.marginTop = '0.5rem';
    note.style.marginBottom = '0.5rem';
    note.style.color = NOTE_COLOR_HEX;
    note.style.fontWeight = 'normal';
    note.style.lineHeight = '1.4';
    note.textContent = `Please note: this order contains a preorder item and is estimated to ship ${shipDate || PREORDER_SHIP_DATE}.`;

    markerEl.parentElement.insertBefore(note, markerEl);

    log('normal-item preorder note injected', { variantId: markerEl.dataset.variantId });
  };



  // ---------- RENDER ----------
  const renderItem = (container, v, qty, unitAllocCents, originalUnitCents) => {
    const li = document.createElement('li');
    li.className = 'bundle-contents__item bundle-chip';
    /* Charms carry a CHA* SKU. Recording it (plus the letter and the allocated unit
     * price) lets mergeCharmChips fold them into one "spells the name" row below. */
    li.dataset.sku  = (v.sku || '');
    li.dataset.vid  = String(v.id || '');
    li.dataset.lttr = (v.title || '');
    li.dataset.unit = String(Number(unitAllocCents) || 0);
    li.dataset.qty  = String(qty);
    /* Carried so mergeCharmChips can sum a line-level compare for the folded row. */
    li.dataset.cmp  = String(Number.isFinite(Number(originalUnitCents)) ? Number(originalUnitCents) : 0);

    const rawName      = (v.name || '').split(' - ')[0] || v.name || 'Item';
    const variantTitle = (v.title && v.title !== 'Default Title') ? ` — ${v.title}` : '';
    const image = imgSrc(v);

    const current  = Number(unitAllocCents) || 0;
    /* The $0 mirrors are named "Free <product>" because on a giveaway that is exactly
     * what they are. On a set the customer is paying for them — the line above shows the
     * price — so the word is wrong. Key it on whether this row is actually being charged:
     * a genuinely free item still costs nothing and keeps its name. */
    /* The mirror products are literally named "Free Waterproof Dog Collar" so the customer
     * can see what a set includes, but in the cart that reads as a giveaway sitting beside
     * the thing they paid for. Strip it everywhere except the charms, where the giveaway is
     * real. */
    const isCharmRow = /^CHA?(\d+-|FREE-)/i.test(String((v && v.sku) || ''));
    const productName = isCharmRow && current === 0 ? rawName : rawName.replace(/^Free\s+/i, '');
    /* The row used to print the UNIT price next to "×N", so at any quantity above one
     * the number shown was not the number being charged and the rows did not add up to
     * the set price. Show the arithmetic instead: unit, quantity, then the line total. */
    const original    = Number(originalUnitCents);
    const lineTotal   = current * qty;
    const lineCompare = (Number.isFinite(original) ? original : 0) * qty;
    const showCompare = lineCompare > lineTotal;

    li.innerHTML = `
      <figure class="bundle-chip__img">
        ${image ? `<img src="${image}${image.includes('?') ? '&' : '?'}width=80" alt="${productName}${variantTitle}" />` : ``}
      </figure>

      <span class="bundle-chip__title" title="${productName}${variantTitle}">
        ${productName}${variantTitle}
      </span>

      <span class="bundle-chip__price-wrap">
        ${qty > 1 && current > 0
          ? `<span class="bundle-chip__calc">${moneyAuto(current)} ea × ${qty}</span>`
          : `<span class="bundle-chip__qty">×${qty}</span>`}
        ${showCompare ? `<span class="bundle-chip__compare">${moneyNoCents(lineCompare)}</span>` : ``}
        ${lineTotal === 0
          ? `<span class="bundle-chip__price bundle-chip__free">FREE</span>`
          : `<span class="bundle-chip__price">${moneyNoCents(lineTotal)}</span>`}
      </span>
    `;
    container.appendChild(li);
  };

  /* The charm builder spells a name one letter at a time, so N separate cart rows
   * ("A", "B", "C") lose the thing the customer actually bought. Fold them into a
   * single row showing the letters side by side in the order they were added — which
   * is the order they appear in _Details, hence DOM order here.
   * Priced at zero (free-charm offers, or $0 variants) it reads FREE. */
  /* _Details is whatever order the bundling app wrote it in — for charms that came
   * back alphabetical, so "JAKE" rendered as A E J K. The truth is _OrderIndex on the
   * charm lines themselves, so read the cart once and order by that. */
  let cartOrderPromise = null;
  const charmOrderFromCart = () => {
    if (!cartOrderPromise) {
      cartOrderPromise = fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then((r) => r.json())
        .then((cart) => (cart.items || [])
          .filter((it) => it.properties && it.properties._Letter != null)
          .map((it) => ({
            vid: String(it.variant_id),
            letter: String(it.properties._Letter || ''),
            idx: Number(it.properties._OrderIndex),
            qty: Number(it.quantity) || 1
          }))
          .filter((x) => Number.isFinite(x.idx))
          .sort((a, b) => a.idx - b.idx))
        .catch(() => []);
    }
    return cartOrderPromise;
  };

  /* Offer sets (sections/offer-lp, "set;" on _FreeUnits): what the customer pays for on
   * top — the named items, then the charms line — and the FREE gifts underneath. */
  const orderOfferSet = (list, wrap) => {
    if (!list || !/^set;/.test(String((wrap && wrap.dataset.freeUnits) || ''))) return;
    const rows = Array.from(list.children);
    const isFree = (li) => !!li.querySelector('.bundle-chip__free');
    const isCharms = (li) => !!li.querySelector('.bundle-chip__charmstrip, .bundle-chip__charmlabel');
    const rank = (li) => (isFree(li) ? 2 : isCharms(li) ? 1 : 0);
    rows.map((li, i) => ({ li, i, r: rank(li) }))
      .sort((a, b) => a.r - b.r || a.i - b.i)
      .forEach(({ li }) => list.appendChild(li));
  };

  const mergeCharmChips = async (list, wrap) => {
    /* Charm SKUs are not consistent across the catalogue: Baby Pink is CHA1027-A
     * while Green is CH1003-A and Lilac is CH1008-A. The CH->CHA migration runs per
     * letter, not per colour, so one name can mix both prefixes, and the old
     * [data-sku^="CHA"] test therefore merged some colours while leaving others as
     * one row per letter in the same cart. Match the family, not one spelling of it.
     *
     * Anchor on what follows the prefix -- digits for a paid charm, FREE- for a $0
     * mirror (CHAFREE-GREEN-A). Accepting "CH" plus anything would also sweep in
     * CH-STRAP, CHM*, CHP*, CHD*, CHSS* and CHCM*, none of which are letter charms. */
    const isCharmSku = (sku) => /^CHA?(\d+-|FREE-)/i.test(String(sku || ''));
    const rows = Array.from(list.querySelectorAll('li.bundle-chip'));
    const chips = rows.filter((c) => isCharmSku(c.dataset.sku));
    if (!chips.length) return;

    /* The strap says "these charms go on that product", so it only makes sense when
     * the product is in the set with them. A charms-only bundle -- the old "Dog Charm
     * Personalization" parent, which is still what the charm PDP and the live theme
     * produce -- has no product to sit on, and collapsing it hid the per-charm lines
     * for no gain. List those out individually, the way live does. */
    /* ...unless the product IS in the bundle and simply is not in the list. A product
     * bought with charms excludes its own parent line from "Set items", which left every
     * chip a charm and skipped the merge — so the charms rendered as bare product rows
     * with their own artwork and no strap at all. The flag says a parent was removed. */
    if (chips.length === rows.length && !(wrap && wrap.dataset.bundleParentProduct === '1')) return;

    /* Each charm carries its own letter artwork, so the row spells the name in the
     * real charms rather than in text. */
    const beads = [];
    const chipVid = [];
    let total = 0, count = 0, totalCompare = 0;
    chips.forEach((c) => {
      const q = Number(c.dataset.qty) || 1;
      const img = c.querySelector('.bundle-chip__img img');
      const src = img ? (img.getAttribute('src') || '') : '';
      for (let i = 0; i < q; i++) { beads.push({ letter: c.dataset.lttr || '', src }); chipVid.push(c.dataset.vid || ''); }
      total += (Number(c.dataset.unit) || 0) * q;
      totalCompare += (Number(c.dataset.cmp) || 0) * q;
      count += q;
    });

    /* Re-order to match how the customer actually typed the name.
     * First choice is _CharmOrder off the parent line — a plain letter sequence the
     * bundling app can't reshuffle. /cart.js is the fallback for carts added before
     * that property existed. */
    let seqRaw = (wrap && wrap.dataset.charmOrder) || '';
    if (!seqRaw) {
      /* The property didn't survive onto any cart line — fall back to what the PDP
       * recorded when it added the set. */
      try { seqRaw = sessionStorage.getItem('mpbCharmOrder') || ''; } catch (e) {}
      if (!seqRaw) { try { seqRaw = localStorage.getItem('mpbCharmOrder') || ''; } catch (e) {} }
    }
    const seq = seqRaw.split(',').map((x) => x.trim()).filter(Boolean);
    if (seq.length) {
      const pool = new Map();
      beads.forEach((b) => {
        const k = String(b.letter || '').toUpperCase();
        if (!pool.has(k)) pool.set(k, []);
        pool.get(k).push(b);
      });
      const rebuilt = [];
      seq.forEach((ch) => {
        const p = pool.get(String(ch).toUpperCase());
        if (p && p.length) rebuilt.push(p.shift());
      });
      pool.forEach((p) => p.forEach((b) => rebuilt.push(b)));
      if (rebuilt.length === beads.length) {
        beads.length = 0; beads.push(...rebuilt);
      }
    }

    const order = seq.length ? [] : await charmOrderFromCart();
    if (order.length) {
      const byVid = new Map();
      beads.forEach((b, i) => {
        const vid = chipVid[i];
        if (!byVid.has(vid)) byVid.set(vid, []);
        byVid.get(vid).push(b);
      });
      const rebuilt = [];
      order.forEach((o) => {
        for (let n = 0; n < o.qty; n++) {
          const pool = byVid.get(o.vid);
          if (pool && pool.length) rebuilt.push(pool.shift());
        }
      });
      // anything the cart didn't account for keeps its original position at the end
      byVid.forEach((pool) => pool.forEach((b) => rebuilt.push(b)));
      if (rebuilt.length === beads.length) { beads.length = 0; beads.push(...rebuilt); }
    }

    const name = beads.map(b => b.letter).filter(Boolean).join(' ');
    const isFree = total === 0;
    /* Same treatment as the PDP strap: the letter artwork only fills ~65% of its
     * canvas, so the box has to be ~1.5x the wanted letter height, and each charm is
     * spaced by its own measured width or the wide shapes collide. */
    const GLYPH_W = {
      A:.70,B:.42,C:.41,D:.42,E:.43,F:.37,G:.50,H:.75,I:.21,J:.39,K:.51,L:.44,M:.64,
      N:.46,O:.48,P:.36,Q:.49,R:.47,S:.42,T:.51,U:.47,V:.45,W:.64,X:.57,Y:.42,Z:.53,
      HEART:.65, STAR:.68, FLOWER:.60
    };
    /* GAP was 3px, which packed the letters shoulder to shoulder — on a five-charm strap
     * they read as one smear. Give them room; the margin below is still per-glyph, so wide
     * letters keep their extra clearance.
     *
     * 12px was still too tight once a strap carried eight charms. fitStrip scales the whole
     * strip down to fit the cart column, and it scales these margins by the same factor —
     * so the gap the maths promises at full size is the FIRST thing to disappear when the
     * strip has to shrink. Set it wide enough that it survives that scaling. */
    const BOX = 52, GAP = 22;  // 52px box on a 26px strap = ~15% proud each side
    const marginFor = (letter) => {
      const fill = GLYPH_W[String(letter || '').toUpperCase()] ?? 0.5;
      /* Never negative. The trim exists to close the transparent padding either side of a
       * narrow glyph, but GLYPH_W measures the LETTER and the charm art around it is wider,
       * so a negative margin pulled the silicone shapes into each other -- measured at -4px
       * per pair on an eight-charm strap, which is the overlap you can see. Clamping at 0
       * costs a little air around narrow letters and guarantees the art never collides. */
      return Math.max(0, ((BOX * fill + GAP) - BOX) / 2);
    };
    const strip = beads.map(b => {
      const style = `width:${BOX}px;height:${BOX}px;margin:0 ${marginFor(b.letter).toFixed(1)}px`;
      return b.src
        ? `<img class="charmstrip__bead" style="${style}" src="${b.src}" alt="${b.letter}" title="${b.letter}" />`
        : `<span class="charmstrip__bead charmstrip__bead--text" style="${style}">${b.letter}</span>`;
    }).join('');

    const li = document.createElement('li');
    li.className = 'bundle-contents__item bundle-chip bundle-chip--charms' +
      (isFree ? ' is-free' : '');
    /* Paint the strap the same colour as the collar and leash. */
    /* Straight off this cart line -- see cart-item.liquid. No storage fallback on
     * purpose: the old session/local key was global, so it survived a refresh with
     * whatever collar was last viewed and handed every bundle in the cart the same
     * colour. An unpainted strap is better than a confidently wrong one. */
    /* Resolve the collar's colour from the SET ITEMS when the line itself cannot say.
     * A bundle sold under a Title-only parent has no colour option to read, and the cart
     * transform rebuilds the merged line's attributes so _StrapHex does not survive
     * either. The children carry it in their names — "Waterproof Dog Collar - Sky Blue /
     * Large" — so take the collar's, and map it through the theme's own swatch map. */
    const __colourFromChildren = () => {
      const map = new Map();
      String((wrap && wrap.dataset.swatchMap) || '').split(/[\r\n]+/).forEach((l) => {
        const i = l.indexOf(':');
        if (i < 0) return;
        const k = l.slice(0, i).trim().toUpperCase();
        const v = l.slice(i + 1).trim();
        if (k && v) map.set(k, v);
      });
      /* Search the whole bundle, not just this list: the charm strip is merged before the
       * component chips are appended, so looking only in `list` found no collar row. */
      const scope = (wrap && wrap.querySelectorAll) ? wrap : list;
      let rows = Array.from(scope.querySelectorAll('li.bundle-chip'));
      if (!rows.length) rows = Array.from(list.querySelectorAll('li.bundle-chip'));
      /* A charm row can never be the collar — and it LOOKS like one.
       *
       * The charm product is called "Silicone Dog Charms | Letters & Symbols for Personalized
       * Dog Collars and Leashes", so /collar/i matched it before the real collar, and the
       * colour was then parsed out of "— R x3 $9". That produced a hex of nothing and a
       * pattern URL of collar-pattern-r-3-3.png, which 404s — which is why a La Lune set
       * showed its flat stand-in in the cart while the popup showed the print. */
      const isCharmish = (r) => r.classList.contains('bundle-chip--charms') ||
        /^CHA?(\d+-|FREE-)/i.test(String((r.dataset && r.dataset.sku) || '')) ||
        /silicone dog charms|letters\s*&\s*symbols/i.test(r.textContent || '');
      rows = rows.filter((r) => !isCharmish(r));
      /* Same order as the PDP: collar first, then the charm strap for bundles with no
       * collar, such as the bottle. Taking the first row gave the bottle's colour. */
      const collar = rows.find((r) => /collar/i.test(r.textContent || ''))
                  || rows.find((r) => /charm[-_ ]?strap/i.test(r.textContent || ''))
                  || null;
      if (!collar) return { hex: '', img: '' };
      /* "<product> - <Colour> / <Size>" — the colour is between the dash and the slash. */
      const m = (collar.textContent || '').split(/\s[-\u2014]\s/)[1] || '';
      const name = m.split('/')[0].trim();
      if (!name) return { hex: '', img: '' };
      const hex = map.get(name.toUpperCase()) || '';
      const base = (wrap && wrap.dataset.patternBase) || '';
      const handle = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      return { hex, img: base ? base + 'collar-pattern-' + handle + '.png' : '' };
    };
    let strapHex = (wrap && wrap.dataset.strapHex) || '';
    if (strapHex) li.style.setProperty('--charm-strap', strapHex);
    /* Printed colourways carry the artwork instead of a hex, so the strip shows the
     * stripes or the dots the customer actually bought. */
    let strapImg = (wrap && wrap.dataset.strapImg) || '';
    /* The SET ITEMS are what is actually in the box, so they outrank the line's own
     * colour option. On a bundle whose parent product carries its own Color — core-walk-set
     * does — the two can disagree: the parent sat on Lilac while the collar and leash in
     * the set were Blueberry, and the strip painted the parent's colour. An explicit
     * _StrapHex still wins, because that is the PDP saying so deliberately. */
    const derived = __colourFromChildren();
    const fromLine = (wrap && wrap.dataset.strapSrc) === 'line';
    if (derived.hex && (!strapHex || fromLine)) strapHex = derived.hex;
    if (derived.img && !strapImg) strapImg = derived.img;
    if (strapHex) li.style.setProperty('--charm-strap', strapHex);
    /* Assigned once the strip exists, below. The late retry needs it. */
    let applyPattern = null;
    if (!strapHex || !strapImg) {
      /* The component chips land after this runs; try again once they exist.
       *
       * This used to re-apply only the HEX, so a printed colourway resolved late — which
       * every set bundle does, its colour comes from the child rows — ended up flat. Cherie
       * showed its pale stand-in blue instead of the print. Retry the artwork too. */
      /* The component chips arrive only after their /variants/{id}.js fetches resolve, so a
       * single 250ms retry was a race: on a set bundle — whose colour can ONLY come from the
       * children, its Title-only parent has no colour option — the rows were often still
       * absent and the strip stayed unpainted. Keep looking until they show up. */
      let tries = 0;
      const retry = () => {
        const late = __colourFromChildren();
        if (late.hex && !strapHex) { strapHex = late.hex; li.style.setProperty('--charm-strap', late.hex); }
        if (late.img && !strapImg && typeof applyPattern === 'function') applyPattern(late.img);
        if ((strapHex && strapImg) || ++tries >= 8) return;
        setTimeout(retry, 300);
      };
      setTimeout(retry, 250);
    }
    li.innerHTML = `
      <span class="bundle-chip__title" title="Charms — ${name}">
        <span class="bundle-chip__charmlabel">Charms</span>
        <span class="bundle-chip__charmstrip">${strip}</span>
      </span>

      <span class="bundle-chip__price-wrap">
        ${wrap && wrap.dataset.charmFlat === '1'
          ? ``
          : count > 1 && !isFree
          ? `<span class="bundle-chip__calc">${moneyAuto(Math.round(total / count))} ea \u00d7 ${count}</span>`
          : `<span class="bundle-chip__qty">\u00d7${count}</span>`}
        ${totalCompare > total
          ? `<span class="bundle-chip__compare">${moneyNoCents(totalCompare)}</span>`
          : ``}
        ${isFree
          ? `<span class="bundle-chip__price bundle-chip__free">FREE</span>`
          : `<span class="bundle-chip__price">${moneyNoCents(total)}</span>`}
      </span>
    `;
    list.insertBefore(li, chips[0]);
    chips.forEach((c) => c.remove());

    /* Two sets of charms are TWO sets, and the cart drew them as one.
     *
     * Everything above builds a single strip from every bead, because the cart only receives
     * `_Details` — variant ids and quantities — and per-item properties never arrive. Adding
     * two sets to one collar therefore came out as a single seven-charm row in one colour,
     * which is neither what was configured nor what will be made. `_CharmSets` carries the
     * counts (in _CharmOrder's order) and `_CharmSetStrap` the colour each set wears, so the
     * beads can be cut back into the sets the customer actually built. */
    const setCounts = String((wrap && wrap.dataset.charmSets) || '')
      .split(',').map((n) => parseInt(n, 10)).filter((n) => n > 0);
    const setStraps = String((wrap && wrap.dataset.charmSetStrap) || '').split(',');
    if (setCounts.length > 1 && setCounts.reduce((a2, b2) => a2 + b2, 0) === beads.length) {
      const strips = Array.from(li.querySelectorAll('.bundle-chip__charmstrip'));
      const host = strips[0];
      if (host) {
        const all = Array.from(host.children);
        let at = 0;
        setCounts.forEach((count, gi) => {
          const target = gi === 0 ? host : host.cloneNode(false);
          if (gi > 0) host.parentNode.insertBefore(target, host.nextSibling);
          /* Stacked straps need air between them or two sets read as one striped bar.
           * Do NOT touch `display` here: the strip centres its beads with its own layout,
           * and forcing `block` dropped every letter below its bar. */
          /* Stacked straps need real air between them: at 4px the charms on the second
           * row nearly touched the first row's, which read as one crowded block. */
          if (gi > 0) target.style.marginTop = '10px';
          const mine = all.slice(at, at + count);
          at += count;
          if (gi > 0) mine.forEach((n) => target.appendChild(n));
          else Array.from(target.children).forEach((n) => { if (mine.indexOf(n) === -1) n.remove(); });
          const paint = (setStraps[gi] || '').trim();
          if (!paint) return;
          if (/^(https?:)?\/\//.test(paint) || /\.(png|jpe?g|webp|gif)/i.test(paint)) {
            const h = target.getBoundingClientRect().height || 26;
            target.style.backgroundImage = 'url("' + paint + '")';
            target.style.backgroundRepeat = 'repeat-x';
            target.style.backgroundPosition = 'center';
            target.style.backgroundSize = /collar-pattern-/.test(paint)
              ? (h * 0.5217).toFixed(1) + 'px ' + h.toFixed(1) + 'px'
              : 'auto ' + h.toFixed(1) + 'px';
          } else {
            target.style.backgroundImage = 'none';
            target.style.background = paint;
          }
        });
        li.dataset.charmSetCount = String(setCounts.length);
      }
    }

    /* Each charm is drawn at a fixed 52px box. Eight of them are wider than the
     * strip gets inside a 414px cart drawer, and the row is nowrap, so a long name
     * ran straight off the right edge. Measure the strip once it is in the document
     * and scale the beads (and the strap they sit on) down to whatever fits. */
    const strap = li.querySelector('.bundle-chip__charmstrip');
    let patternOk = false;
    if (strap) {
      const STRAP_H = 26, PAD = 14;
      /* Fit EVERY strap, and size each one against ITS OWN charms.
       *
       * This measured `beads` — every charm in the bundle — and applied the result to the
       * first strap only. Two sets therefore shrank each other: five charms split 2 and 3
       * were scaled as if all five sat on one strap, and the cloned strap kept whatever
       * inline size it was born with, so the two rows came out different heights. Worse,
       * `beads[i]` indexed the GLOBAL list while `i` counted within one strap, so every
       * strap after the first got another letter's margins.
       *
       * Reading the letter back off each bead keeps this honest no matter how the strips
       * were split. */
      const letterOf = (el) => String(
        el.getAttribute('title') || el.getAttribute('alt') || el.textContent || '').trim();
      let lastFit = -1;
      const fitStrip = () => {
        const avail = strap.clientWidth - PAD * 2;
        if (avail <= 0 || avail === lastFit) return;
        lastFit = avail;
        let k = 1;
        li.querySelectorAll('.bundle-chip__charmstrip').forEach((row) => {
          const els = Array.from(row.querySelectorAll('.charmstrip__bead'));
          if (!els.length) return;
          const nat = els.reduce((w, el) => w + BOX + 2 * marginFor(letterOf(el)), 0);
          const room = (row.clientWidth || strap.clientWidth) - PAD * 2;
          const rowK = room > 0 ? Math.min(1, room / nat) : 1;
          k = Math.min(k, rowK);
          els.forEach((el) => {
            el.style.width = (BOX * rowK).toFixed(1) + 'px';
            el.style.height = (BOX * rowK).toFixed(1) + 'px';
            el.style.margin = '0 ' + (marginFor(letterOf(el)) * rowK).toFixed(1) + 'px';
          });
          row.style.height = (STRAP_H * rowK).toFixed(1) + 'px';
          row.style.padding = '0 ' + (PAD * rowK).toFixed(1) + 'px';
        });
        if (strapImg && patternOk) {
          /* The tile is 13.07mm long by 25.05mm across, so at this height one tile is
           * 0.52 of it wide -- which keeps the dots round and the stripes the right
           * pitch however far the strip has had to shrink. */
          const h = STRAP_H * k;
          strap.style.backgroundImage = 'url("' + strapImg + '")';
          strap.style.backgroundRepeat = 'repeat-x';
          strap.style.backgroundPosition = 'center';
          /* Two kinds of tile arrive here and they are NOT sized the same way. A
           * collar-pattern-*.png is the physical webbing at 13.07mm by 25.05mm, so it takes
           * the 0.52 ratio above. A swatch-*.png is a true repeat unit cut from the vector
           * artwork (Milos is 3.42:1), and forcing that through 0.52 squashes the wave into a
           * sliver. The filename is the contract: the swatch- namespace exists precisely
           * because product media had already claimed the bare names. */
          strap.style.backgroundSize = /collar-pattern-/.test(strapImg)
            ? (h * 0.5217).toFixed(1) + 'px ' + h.toFixed(1) + 'px'
            : 'auto ' + h.toFixed(1) + 'px';
        }
      };
      /* The tile is named by convention from the colour, so most colours 404 here.
       * Load it before painting; the hex stands until it arrives. */
      /* Paint on load, do not delegate to fitStrip.
       *
       * fitStrip is driven by resize and bails on `avail <= 0 || avail === lastFit`, so a
       * pattern that resolved after the strip had already settled at its final width was
       * dropped on the floor — La Lune and Cherie showed their flat stand-in in the cart
       * while the popup showed the print. Set the background here and let fitStrip carry on
       * doing the scaling. */
      const paintTile = (url) => {
        const h = strap.getBoundingClientRect().height || STRAP_H;
        strap.style.backgroundImage = 'url("' + url + '")';
        strap.style.backgroundRepeat = 'repeat-x';
        strap.style.backgroundPosition = 'center';
        strap.style.backgroundSize = /collar-pattern-/.test(url)
          ? (h * 0.5217).toFixed(1) + 'px ' + h.toFixed(1) + 'px'
          : 'auto ' + h.toFixed(1) + 'px';
      };
      applyPattern = function (url) {
        if (!url || strapImg === url) return;
        strapImg = url;
        const probe = new Image();
        probe.onload = function () { patternOk = true; lastFit = -1; paintTile(url); fitStrip(); };
        probe.src = url;
      };
      if (strapImg) {
        const url = strapImg;
        const probe = new Image();
        probe.onload = function () { patternOk = true; lastFit = -1; paintTile(url); fitStrip(); };
        probe.src = url;
      }
      requestAnimationFrame(fitStrip);
      /* The drawer measures zero while it is closed, and /cart is a different width
       * again, so re-fit whenever the row is actually given a size. */
      try { new ResizeObserver(fitStrip).observe(strap); } catch (e) {}
    }
  };

  /* What the set would cost bought separately. The $0 charm mirrors carry their real
   * $3 in compare_at_price, so this needs no hard-coded charm price — and it stays
   * right if the price ever changes. */
  const valueOf = (items) => items.reduce((sum, it) => it.ok
    ? sum + (it.unitCompare > it.unitPrice ? it.unitCompare : it.unitPrice) * it.qty
    : sum, 0);

  /* The line reads "$63" with no hint that the charms were worth $21 of it. Put the
   * separate-purchase value beside it, struck through, the way the theme shows any
   * other compare-at price. */
  const paintLinePrice = (wrap, paid, value) => {
    const row = wrap.closest('cart-item');
    if (!row) return;
    const priceEl = row.querySelector('.price');
    if (!priceEl) return;
    let was = priceEl.querySelector('.bundle-value-was');
    if (!(value > paid)) { if (was) was.remove(); return; }
    if (!was) {
      was = document.createElement('s');
      was.className = 'price__strikethrough bundle-value-was';
      priceEl.appendChild(was);
    }
    was.textContent = moneyNoCents(value);
    /* Shopify already strikes the line's own compare-at — the parent variant's — and we
     * add the set's real worth beside it, which left two struck figures. Ours is the one
     * that counts: it knows the contents, charms included, where the line's compare only
     * knows the parent. Hide the theme's, only on lines we paint. */
    priceEl.querySelectorAll('s, .price__strikethrough').forEach((el) => {
      if (el !== was) el.style.display = 'none';
    });
  };

  /* Same story one level up: the cart's own total. Summed across every bundle on the
   * page, so two personalised sets both count. */
  const paintSubtotal = () => {
    const wraps = document.querySelectorAll('.bundle-contents[data-bundle-value]');
    let saved = 0;
    wraps.forEach((w) => {
      saved += Math.max(0, Number(w.dataset.bundleValue || 0) - Number(w.dataset.bundlePaid || 0));
    });
    /* Two different subtotals ship: the cart page uses js-inline-subtotal, the drawer
     * footer uses js-cart-footer-subtotal, and only the second one is on screen in
     * the drawer -- painting just the first left the visible total untouched. */
    document.querySelectorAll('[js-inline-subtotal], [js-cart-footer-subtotal]').forEach((box) => {
      const old = box.querySelector('.cart-subtotal__was');
      if (!saved) { if (old) old.remove(); return; }
      /* The rendered subtotal is the only place the cart's real total is written into
       * the DOM, so read it back rather than fetching /cart.js again. */
      /* Two spans are rendered, one with the currency code and one without, and only
       * one of them is on screen. Cross out the one the customer can actually see. */
      /* Skip spans inside the label: with a discount applied the drawer footer prints
       * "(Discount: -$9)" there, and reading that as the total painted "$42" (9 + 33 saved). */
      const spans = Array.prototype.slice.call(box.querySelectorAll('span'))
        .filter((el) => !el.classList.contains('cart-subtotal__was') && !el.closest('label'));
      const valueEl = spans.filter((el) => el.offsetParent !== null)[0] || spans[0];
      if (!valueEl) return;
      const cents = Math.round(parseFloat(String(valueEl.textContent).replace(/[^0-9.]/g, '')) * 100);
      if (!Number.isFinite(cents) || cents <= 0) return;
      const was = old || document.createElement('s');
      was.className = 'cart-subtotal__was';
      was.textContent = moneyNoCents(cents + saved);
      if (!old) valueEl.parentNode.insertBefore(was, valueEl);
    });
  };

  /* The drawer renders its items and its summary in separate passes, and re-renders
   * the summary on every quantity change — each time dropping the cross-out. Watch
   * for it going missing rather than guessing at timeouts. Our own insert can't
   * retrigger this, because by then the marker is present. */
  /* Run the set items the full width of the cart card. They live several wrappers
   * deep inside the text column, so the indent is not one fixed number -- measure it
   * against the card and pull back by exactly that, re-measuring whenever the row is
   * resized (the drawer and the cart page are different widths). */
  const spanCard = (wrap) => {
    const card = wrap.closest('.product-item-minimal');
    if (!card) return;
    const apply = () => {
      wrap.style.marginLeft = '';
      wrap.style.width = '';
      wrap.style.marginTop = '';
      const cs = getComputedStyle(card);
      const padL = parseFloat(cs.paddingLeft) || 0;
      const padR = parseFloat(cs.paddingRight) || 0;
      const inner = card.clientWidth - padL - padR;
      const shift = wrap.getBoundingClientRect().left -
                    (card.getBoundingClientRect().left + padL);
      if (!(inner > 0) || shift <= 1) return;
      wrap.style.marginLeft = (-shift).toFixed(1) + 'px';
      wrap.style.width = inner.toFixed(1) + 'px';
      /* Now that it spans the whole card it can run under the thumbnail, which is
       * taller than the title and price stacked beside it -- on a wide card the
       * "Set items" label landed on top of the image. Drop below whichever is lower. */
      const img = card.querySelector('.product-item-minimal__image');
      if (img) {
        const clear = img.getBoundingClientRect().bottom -
                      wrap.getBoundingClientRect().top;
        if (clear > 0) wrap.style.marginTop = (clear + 8).toFixed(1) + 'px';
      }
    };
    requestAnimationFrame(apply);
    try { new ResizeObserver(() => requestAnimationFrame(apply)).observe(card); } catch (e) {}
  };

  const watchSubtotal = () => {
    if (window.__bhSubtotalWatch) return;
    window.__bhSubtotalWatch = 1;
    const host = document.querySelector('cart-drawer') ||
                 document.querySelector('[js-cart-items]') || document.body;
    try {
      new MutationObserver(() => {
        if (document.querySelector('[js-inline-subtotal], [js-cart-footer-subtotal]') &&
            !document.querySelector('.cart-subtotal__was')) paintSubtotal();
      }).observe(host, { childList: true, subtree: true });
    } catch (e) {}
  };

  // ---------- HYDRATE ----------
  /* Show the set's value anchor above the contents, matching the PDP. */
  const renderSetValue = (wrap, paidCents) => {
    const raw = Number(wrap.dataset.setValue || 0);
    if (!raw || raw <= paidCents) return;
    if (wrap.querySelector('.bundle-contents__value')) return;
    const el = document.createElement('p');
    el.className = 'bundle-contents__value';
    el.innerHTML = `<s>${moneyNoCents(raw)}</s> <span>value</span>`;
    const label = wrap.querySelector('.bundle-contents__label');
    if (label) label.parentNode.insertBefore(el, label);
    else wrap.insertBefore(el, wrap.firstChild);
  };

  const hydrate = async (wrap) => {
    const list = wrap.querySelector('.bundle-contents__list');
    if (!list) return;
    if (wrap.dataset.hydrated === '1') return; // hydrate once per render
    wrap.dataset.hydrated = '1';
    list.innerHTML = '';

    const pairs = parseDetails(wrap.dataset.bundleDetails || '');
    log('details parsed', pairs);
    if (!pairs.length) { log('no variant IDs parsed; aborting hydrate'); return; }

    // For ONE bundle (not count of bundles)
    const lineFinal = parseInt(wrap.dataset.bundleFinal || '0', 10) || 0;
    const lineQty   = Math.max(1, parseInt(wrap.dataset.bundleQty || '1', 10));
    const perBundleFinal = Math.round(lineFinal / lineQty);
    log('bundle totals', { lineFinal, lineQty, perBundleFinal });

    // Fetch variants from /variants/{id}.js
    const results = await Promise.all(pairs.map(async ({ id, qty }) => {
      try {
        const v = await fetchVariant(id);
        log('fetched variant', id, v);
        return { ok: true, v, qty };
      } catch (e) {
        console.warn('[BundleHydrator] fetch failed', id, e);
        return { ok: false, id, qty };
      }
    }));

    // Fetch Storefront flags (currentlyNotInStock) for OK variants
    let items = await Promise.all(results.map(async (r) => {
      if (!r.ok) return r;

      const sf = await fetchStorefrontFlags(String(r.v.id));
      log('storefront flags', { id: r.v.id, ...sf });

      return {
        ok: true,
        v: r.v,
        qty: r.qty,
        unitPrice: asNumber(r.v.price, 0),               // cents
        unitCompare: asNumber(r.v.compare_at_price, 0),  // cents (display-only)
        preorderish: isPreorderish(r.v, sf)
      };
    }));

    // Preorder: if ANY component is currentlyNotInStock (or fallback says preorderish),
    // inject a note UNDER the pills for the bundle.
    /* The PARENT is not a thing that ships.
     *
     * A set's parent is a virtual bundle with no stock of its own, so Shopify reports it
     * `currentlyNotInStock: true` and the whole line claimed to be a preorder — on sets
     * whose components were all in stock. Only what actually ships can put a bundle on
     * backorder, which is the same reason the parent is kept out of the "Set items" list. */
    const preorderParentVid = Number(wrap.dataset.parentVariant || 0) || 0;
    const preorderItems = items.filter(
      it => it.ok && it.preorderish && Number(it.v.id) !== preorderParentVid);
    const anyComponentPreorder = preorderItems.length > 0;
    log('anyComponentPreorder', anyComponentPreorder, { excludedParent: preorderParentVid });
    if (anyComponentPreorder) {
      /* The latest date among the components actually on backorder — a bundle cannot ship
       * before its slowest part, and a per-SKU override is what makes that differ. */
      const dates = preorderItems.map(it => preorderDateForSku(it.v && it.v.sku));
      injectPreorderNote(wrap, dates[dates.length - 1]);
    }

    /* Component sets: the parent product is itself a line, carrying the set price, and the
     * components are $0 mirrors staged only so the merged line adds up.
     *
     * Two things follow. The parent must not appear in its own "Set items" list — it is
     * the set, not a part of it — and leaving it in also double-counted the value, which
     * is where the second struck price came from ($63 for the parent plus $63 for the
     * components = $126). And the components must not read FREE: the customer is paying
     * for them, so show each one's share of what was actually charged, weighted by what
     * it is worth. Those shares add up to the line total exactly.
     *
     * A giveaway set is the other shape — the parent is display-only and is not among the
     * details at all — so none of this fires and a free charm still reads FREE. */
    const parentVid = Number(wrap.dataset.parentVariant || 0) || 0;
    const isComponentSet = parentVid > 0 && items.some((it) => it.ok && Number(it.v.id) === parentVid);
    let listItems = items;
    let fullValueItems = items;
    /* Redistributing the line price across the listed components is only right when the
     * parent IS the price and the components are $0 mirrors. A product bought WITH charms
     * is the other shape sharing this branch: the parent is a real priced component and the
     * charms carry their own $3. Handing them the whole line turned a $34 Sky Blue collar
     * with 3 charms into "3 x $11", and a $44 leash with 2 charms into "2 x $22" — the
     * amount charged was right both times, only the breakdown was invented. */
    const payableNonParent = items.filter((it) => it.ok && Number(it.v.id) !== parentVid);
    /* The two kinds of component, told apart by price rather than by guesswork:
     *   MIRRORS ($0)  — what the parent's price actually buys. They must show their share
     *                   of it, never "FREE": the customer is paying $69 for that collar and
     *                   leash, and calling them free both misprices the set and cheapens it.
     *   EXTRAS  (>$0) — charms and the like, riding along at their own price.
     * Treating the bundle as all-one-or-the-other was the bug: a set WITH charms failed the
     * "every component is $0" test, so nothing was redistributed and the mirrors rendered
     * free beside a $69 line. */
    /* Genuinely free lines, named by the PDP on _FreeUnits ("variantId:qty,...").
     *
     * These are the free units of a volume tier. They must NOT take a share of the parent's
     * price — the customer is not paying for them — and they must still be listed, because
     * the customer chose a colour for each one. Before this they were treated as ordinary
     * mirrors, given a zero share once the charms had absorbed the line, and dropped from
     * the breakdown: a "Buy 2, Get 1 Free" cart showed two leashes. */
    const freeIds = new Set(
      /* An offer set (sections/offer-lp) prefixes its list with "set;" — strip it before
       * parsing; it only marks the bundle as a set rather than a volume tier, below. */
      String((wrap && wrap.dataset.freeUnits) || '').replace(/^set;/, '')
        .split(',')
        .map((pair) => parseInt(String(pair).split(':')[0], 10))
        .filter((n) => n > 0));
    const isGenuinelyFree = (it) => freeIds.has(Number(it.v.id));
    payableNonParent.forEach((it) => { if (isGenuinelyFree(it)) it.isFreeUnit = true; });
    const mirrorItems = payableNonParent.filter((it) => it.unitPrice === 0 && !isGenuinelyFree(it));
    const extraItems = payableNonParent.filter((it) => it.unitPrice > 0 && !isGenuinelyFree(it));
    const extraIds = new Set(extraItems.map((it) => Number(it.v.id)));
    let renderRaw = false;
    if (isComponentSet) {
      /* A VOLUME bundle's parent is itself one of the units the customer chose a colour for,
       * so hiding it leaves "Buy 2, Get 1 Free" listing two leashes for three units — the
       * third is only implied by the line header. Component sets are the other shape: there
       * the parent is the set itself and listing it beside its own components double-counts.
       * Free units are the tell, because only a volume tier ships them. */
      const isVolumeBundle = freeIds.size > 0 &&
        !/^set;/.test(String((wrap && wrap.dataset.freeUnits) || ''));
      listItems = isVolumeBundle
        ? items
        : items.filter((it) => !(it.ok && Number(it.v.id) === parentVid));
      /* Tell mergeCharmChips the bundle has a product even though the list will not. */
      wrap.dataset.bundleParentProduct = '1';
      /* Whatever the parent's price did not go on the extras is what it bought the mirrors
       * with. Share that out by what each mirror is worth; the extras keep their own price. */
      const extrasTotal = extraItems.reduce((sum, it) => sum + it.unitPrice * it.qty, 0);
      const weight = mirrorItems.reduce(
        (sum, it) => sum + (it.unitCompare || it.unitPrice) * it.qty, 0);
      const pot = Math.max(0, (perBundleFinal > 0 ? perBundleFinal : weight + extrasTotal) - extrasTotal);
      /* An offer set (sections/offer-lp, "set;" on _FreeUnits) sells named items at their
       * own price and charms as one flat charge: collar $25 + leash $38 leaves $24 for the
       * name, however many letters it has. Proportional shares turned that into "$3.22 ea". */
      const isOfferSet = /^set;/.test(String((wrap && wrap.dataset.freeUnits) || ''));
      const isCharmItem = (it) => /^CHA?(\d+-|FREE-)/i.test(String((it.v && it.v.sku) || ''));
      const fixedItems = mirrorItems.filter((it) => !isCharmItem(it));
      const charmItems = mirrorItems.filter(isCharmItem);
      const fixedTotal = fixedItems.reduce((sum, it) => sum + (it.unitCompare || it.unitPrice) * it.qty, 0);
      const charmUnits = charmItems.reduce((sum, it) => sum + it.qty, 0);
      const flatOk = isOfferSet && fixedTotal <= pot && (charmUnits > 0 || fixedTotal === pot);
      if (flatOk) {
        fixedItems.forEach((it) => { it.unitPrice = it.unitCompare || it.unitPrice; });
        const rest = pot - fixedTotal;
        let left = rest;
        charmItems.forEach((it, i) => {
          const share = i === charmItems.length - 1 ? left : Math.round(rest * it.qty / charmUnits);
          left -= share;
          it.unitPrice = it.qty > 0 ? share / it.qty : share;
        });
        wrap.dataset.charmFlat = '1';
      }
      let spent = 0;
      if (!flatOk) mirrorItems.forEach((it, i) => {
        const w = (it.unitCompare || it.unitPrice) * it.qty;
        /* Last one absorbs the rounding so the shares total the line exactly. */
        const share = i === mirrorItems.length - 1
          ? Math.max(0, pot - spent)
          : (weight > 0 ? Math.round(pot * w / weight) : 0);
        spent += share;
        it.unitPrice = it.qty > 0 ? Math.round(share / it.qty) : share;
      });
      /* Prices are final now — skip the proportional pass below, which would scale them a
       * second time against a total that already includes the extras. */
      renderRaw = true;
      items = listItems;
    }

    const priceSum = items.reduce((s, it) => it.ok ? s + it.unitPrice * it.qty : s, 0);
    const paidCents = perBundleFinal > 0 ? perBundleFinal : priceSum;
    /* The struck-through "worth".
     *
     * On a mixed bundle — the parent carries the set price and real-priced extras ride
     * along with it — the $0 mirrors ARE what the parent's price already covers. Counting
     * them again on top of the parent's compare-at double-counted the whole set: a $75 line
     * read as "$141 worth", which is the parent's $63, plus the same $63 of mirrors, plus
     * $15 of charms. Count the parent once, then only what is genuinely extra. */
    let valueCents;
    if (renderRaw) {
      const parentItem = fullValueItems.find((it) => it.ok && Number(it.v.id) === parentVid);
      const parentVal = parentItem
        ? (parentItem.unitCompare > parentItem.unitPrice ? parentItem.unitCompare : parentItem.unitPrice) * parentItem.qty
        : 0;
      /* Match on the ids captured BEFORE the mirrors were repriced — they are no longer $0,
       * so a `unitPrice > 0` test would now sweep them back in and double-count the set. */
      const extras = fullValueItems.filter((it) => it.ok && extraIds.has(Number(it.v.id)));
      valueCents = parentVal + valueOf(extras);
    } else {
      valueCents = valueOf(items);
    }
    wrap.dataset.bundlePaid = String(paidCents);
    wrap.dataset.bundleValue = String(valueCents);
    paintLinePrice(wrap, paidCents, valueCents);
    /* The drawer renders its summary after the items, so the first pass can land
     * before the subtotal exists — and a later re-render drops what we added. Each
     * call reuses whatever is already there, so repeating it is free. */
    paintSubtotal();
    watchSubtotal();
    spanCard(wrap);
    renderSetValue(wrap, paidCents);
    if (renderRaw || !(perBundleFinal > 0 && priceSum > 0)) {
      log('allocation skipped; render raw items', { priceSum, perBundleFinal, renderRaw });
      for (const it of items) {
        if (!it.ok) {
          const li = document.createElement('li');
          li.className = 'bundle-contents__item';
          li.textContent = `Item ${it.id} × ${it.qty}`;
          list.appendChild(li);
        } else {
          const displayOriginal = it.unitCompare > it.unitPrice ? it.unitCompare : it.unitPrice;
          renderItem(list, it.v, it.qty, it.unitPrice, displayOriginal);
        }
      }
      await mergeCharmChips(list, wrap);
      orderOfferSet(list, wrap);
      return;
    }

    // Proportional allocation (per-unit)
    const ratio = perBundleFinal / priceSum;
    const work = items.filter(it => it.ok).map((it, idx) => ({
      ...it,
      idx,
      rawAlloc: it.unitPrice * ratio
    }));

    // Rounding
    if (ROUND_TO_CENTS) {
      const order = [...work].sort((a, b) =>
        ROUND_CHEAPEST_FIRST ? (a.unitPrice - b.unitPrice || a.idx - b.idx) : (a.idx - b.idx)
      );
      order.forEach(it => { it.unitAlloc = roundCents(it.rawAlloc); });

      // Fix rounding drift
      const currentTotal = order.reduce((s, it) => s + it.unitAlloc * it.qty, 0);
      const diff = perBundleFinal - currentTotal;
      if (diff) {
        const target = order[order.length - 1];
        target.unitAlloc = Math.max(0, target.unitAlloc + Math.round(diff / target.qty));
      }
    } else {
      work.forEach(it => { it.unitAlloc = Math.round(it.rawAlloc); });
      const subtotal = work.reduce((s, it) => s + it.unitAlloc * it.qty, 0);
      const diff = perBundleFinal - subtotal;
      if (diff) {
        const last = work[work.length - 1];
        last.unitAlloc = Math.max(0, last.unitAlloc + Math.round(diff / last.qty));
      }
    }

    // Render in original order
    const byIdx = new Map(work.map(it => [it.idx, it]));
    for (let i = 0; i < items.length; i++) {
      const base = items[i];
      if (!base.ok) {
        const li = document.createElement('li');
        li.className = 'bundle-contents__item';
        li.textContent = `Item ${base.id} × ${base.qty}`;
        list.appendChild(li);
        continue;
      }
      const it = byIdx.get(i);
      const displayOriginal = base.unitCompare > base.unitPrice ? base.unitCompare : base.unitPrice;
      renderItem(list, base.v, base.qty, it.unitAlloc, displayOriginal);
    }
    await mergeCharmChips(list, wrap);
      orderOfferSet(list, wrap);
  };

  const hydrateNormalItems = async (scope = document) => {
    const markers = qa('.preorder-check[data-variant-id]', scope);

    for (const marker of markers) {
      // prevent duplicate work across rerenders
      if (marker.dataset.checked === '1') continue;
      marker.dataset.checked = '1';

      const variantId = (marker.dataset.variantId || '').trim();
      if (!variantId) continue;

      const sf = await fetchStorefrontFlags(variantId);
      log('normal-item storefront flags', { id: variantId, ...sf });

      if (sf.currentlyNotInStock === true) {
        injectPreorderNoteAboveMarker(marker);
      }
    }
  };


  // ---------- BOOT + INTEGRATIONS ----------
  const run = (scope = document) => {
    if (shouldDebug()) console.log('[BundleHydrator] run()', scope === document ? 'document' : scope);

    // Bundles
    qa('.bundle-contents[data-bundle-details]', scope).forEach(hydrate);

    // Normal items
    hydrateNormalItems(scope);
  };

  const ready = () => run();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready, { once: true });
  } else {
    ready();
  }

  // Shopify section lifecycle (theme editor + dynamic sections)
  ['shopify:section:load','shopify:section:reorder','shopify:section:select','shopify:section:deselect']
    .forEach(evt => document.addEventListener(evt, () => run()));

  // Common cart/drawer events across themes
  ['cart:open','cart:rendered','cart:updated','ajaxCart:afterCartLoad','cart:change','cart:refresh']
    .forEach(evt => document.addEventListener(evt, () => run()));

  // Observe the drawer container specifically (when present)
  const drawer =
    document.querySelector('[data-cart-drawer]') ||
    document.querySelector('.cart-drawer') ||
    document.querySelector('cart-drawer');

  if (drawer) {
    new MutationObserver(() => run(drawer))
      .observe(drawer, { childList: true, subtree: true });
  }

  // Fallback: observe the whole document for swapped cart HTML
  new MutationObserver((muts) => {
    if (muts.some(m => m.addedNodes && m.addedNodes.length)) run();
  }).observe(document.body, { childList: true, subtree: true });

  // Optional heartbeat while drawer is open (debug only)
  if (shouldDebug()) {
    setInterval(() => {
      const openDrawer = document.querySelector('[data-cart-drawer].is-open, .cart-drawer.is-open, cart-drawer[open]');
      if (openDrawer) run(openDrawer);
    }, 1200);
  }

  // Public debug hook
  window.BundleHydrator = window.BundleHydrator || {};
  window.BundleHydrator.run = () => run();
})();
